import { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ScrollArea } from '../components/ui/scroll-area';
import { 
  MessageCircle, Plus, Send, Image, Users, User, 
  Trash2, UserPlus, UserMinus, X, ChevronLeft
} from 'lucide-react';
import { toast } from 'sonner';
import { getErrorMessage } from '../lib/errors';

export default function ChatPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [threads, setThreads] = useState([]);
  const [selectedThread, setSelectedThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sendingMessage, setSendingMessage] = useState(false);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  
  // Dialog states
  const [showCreateThread, setShowCreateThread] = useState(false);
  const [showInviteMembers, setShowInviteMembers] = useState(false);
  
  const isManager = ['admin', 'supervisor', 'super_admin', 'branch_manager'].includes(user?.role);

  const loadThreads = useCallback(async () => {
    try {
      const { data } = await api.get('/chat/threads');
      setThreads(data);
      // Auto-select thread from URL parameter
      const threadParam = searchParams.get('thread');
      if (threadParam && !selectedThread) {
        const found = data.find(t => t.id === threadParam);
        if (found) setSelectedThread(found);
      }
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [searchParams, selectedThread]);

  const loadMessages = useCallback(async (threadId) => {
    try {
      const { data } = await api.get(`/chat/threads/${threadId}/messages`);
      setMessages(data);
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);

      // Mark thread as read
      api.post(`/chat/threads/${threadId}/mark-read`).then(() => {
        if (window.__refreshTopBarCounts) window.__refreshTopBarCounts();
      }).catch(() => {});
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  }, []);

  useEffect(() => {
    loadThreads();
  }, [loadThreads]);

  useEffect(() => {
    if (selectedThread) {
      loadMessages(selectedThread.id);
      // Poll for new messages every 5 seconds
      const interval = setInterval(() => loadMessages(selectedThread.id), 5000);
      return () => clearInterval(interval);
    }
  }, [selectedThread, loadMessages]);

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedThread) return;
    
    setSendingMessage(true);
    try {
      const { data } = await api.post(`/chat/threads/${selectedThread.id}/messages`, {
        message_type: 'text',
        text: newMessage.trim()
      });
      setMessages(prev => [...prev, data]);
      setNewMessage('');
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      // Refresh top bar counts (new message may affect other users' counts, but also re-marks our read)
      if (window.__refreshTopBarCounts) window.__refreshTopBarCounts();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSendingMessage(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedThread) return;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error(t('chat.imageOnly'));
      return;
    }
    
    // Upload image
    const formData = new FormData();
    formData.append('file', file);
    formData.append('entity_type', 'chat_message');
    formData.append('entity_id', selectedThread.id);
    
    try {
      const uploadRes = await api.post('/attachments/upload', formData);
      // Get signed/direct URL via download endpoint
      const urlRes = await api.get(`/attachments/${uploadRes.data.id}/download`);
      const imageUrl = urlRes.data.url;
      
      // Send image message
      const { data } = await api.post(`/chat/threads/${selectedThread.id}/messages`, {
        message_type: 'image',
        image_url: imageUrl
      });
      setMessages(prev => [...prev, data]);
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
    
    e.target.value = '';
  };

  const handleDeleteThread = async () => {
    if (!selectedThread || !window.confirm(t('chat.confirmDelete'))) return;
    
    try {
      await api.post(`/chat/threads/${selectedThread.id}/delete`);
      setSelectedThread(null);
      loadThreads();
      toast.success(t('chat.threadDeleted'));
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  const formatTime = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit',
      timeZone: 'Asia/Riyadh'
    });
  };

  const formatDate = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      timeZone: 'Asia/Riyadh'
    });
  };

  return (
    <div data-testid="chat-page" className="h-[calc(100vh-120px)] flex gap-4">
      {/* Thread List */}
      <Card className="w-80 flex-shrink-0 bg-card border-border flex flex-col">
        <CardHeader className="pb-3 flex-shrink-0">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg font-barlow flex items-center gap-2">
              <MessageCircle className="w-5 h-5 text-brand" />
              {t('chat.title')}
            </CardTitle>
            {isManager && (
              <Button variant="ghost" size="sm" onClick={() => setShowCreateThread(true)}>
                <Plus className="w-4 h-4" />
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="flex-1 overflow-hidden p-0">
          <ScrollArea className="h-full">
            <div className="px-4 pb-4 space-y-2">
              {threads.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-8">{t('chat.noThreads')}</p>
              ) : (
                threads.map(thread => (
                  <div
                    key={thread.id}
                    data-testid={`thread-${thread.id}`}
                    onClick={() => setSelectedThread(thread)}
                    className={`p-3 rounded-lg cursor-pointer transition-colors ${
                      selectedThread?.id === thread.id 
                        ? 'bg-brand/10 border border-brand/30' 
                        : 'bg-muted/30 hover:bg-muted/50'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        thread.type === 'group' ? 'bg-purple-500/10' : 'bg-blue-500/10'
                      }`}>
                        {thread.type === 'group' ? (
                          <Users className="w-5 h-5 text-purple-500" />
                        ) : (
                          <User className="w-5 h-5 text-blue-500" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-foreground truncate">{thread.title}</p>
                        <p className="text-xs text-muted-foreground truncate">
                          {thread.member_names?.slice(0, 3).join(', ')}
                        </p>
                        {thread.last_message && (
                          <p className="text-xs text-muted-foreground truncate mt-1">
                            {thread.last_message.text || `[${t('chat.image')}]`}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </ScrollArea>
        </CardContent>
      </Card>

      {/* Chat Area */}
      <Card className="flex-1 bg-card border-border flex flex-col">
        {selectedThread ? (
          <>
            {/* Chat Header */}
            <CardHeader className="pb-3 flex-shrink-0 border-b border-border">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Button variant="ghost" size="sm" className="md:hidden" onClick={() => setSelectedThread(null)}>
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    selectedThread.type === 'group' ? 'bg-purple-500/10' : 'bg-blue-500/10'
                  }`}>
                    {selectedThread.type === 'group' ? (
                      <Users className="w-5 h-5 text-purple-500" />
                    ) : (
                      <User className="w-5 h-5 text-blue-500" />
                    )}
                  </div>
                  <div>
                    <p className="font-medium text-foreground">{selectedThread.title}</p>
                    <p className="text-xs text-muted-foreground">
                      {selectedThread.member_count} {t('chat.members')}
                    </p>
                  </div>
                </div>
                {isManager && selectedThread.type === 'group' && (
                  <div className="flex gap-2">
                    <Button variant="ghost" size="sm" onClick={() => setShowInviteMembers(true)}>
                      <UserPlus className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={handleDeleteThread} className="text-red-500 hover:text-red-600">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </div>
            </CardHeader>

            {/* Messages */}
            <CardContent className="flex-1 overflow-hidden p-0">
              <ScrollArea className="h-full">
                <div className="p-4 space-y-4">
                  {messages.map((msg, idx) => {
                    const isMe = msg.sender_id === user?.id;
                    const showDate = idx === 0 || formatDate(messages[idx-1]?.created_at) !== formatDate(msg.created_at);
                    
                    return (
                      <div key={msg.id}>
                        {showDate && (
                          <div className="text-center text-xs text-muted-foreground my-4">
                            {formatDate(msg.created_at)}
                          </div>
                        )}
                        <div className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}>
                          <div className={`max-w-[70%] ${isMe ? 'items-end' : 'items-start'}`}>
                            {!isMe && (
                              <p className="text-xs text-muted-foreground mb-1">{msg.sender_name}</p>
                            )}
                            <div className={`rounded-2xl px-4 py-2 ${
                              isMe 
                                ? 'bg-brand text-brand-foreground rounded-br-sm' 
                                : 'bg-muted rounded-bl-sm'
                            }`}>
                              {msg.message_type === 'image' ? (
                                <img 
                                  src={msg.image_url} 
                                  alt="Shared" 
                                  className="max-w-full rounded-lg max-h-60 object-contain"
                                />
                              ) : (
                                <p className="text-sm whitespace-pre-wrap">{msg.text}</p>
                              )}
                            </div>
                            <p className={`text-xs text-muted-foreground mt-1 ${isMe ? 'text-end' : ''}`}>
                              {formatTime(msg.created_at)}
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  <div ref={messagesEndRef} />
                </div>
              </ScrollArea>
            </CardContent>

            {/* Message Input */}
            <div className="p-4 border-t border-border flex-shrink-0">
              <div className="flex gap-2">
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleImageUpload}
                  accept="image/*"
                  className="hidden"
                />
                <Button variant="ghost" size="icon" onClick={() => fileInputRef.current?.click()}>
                  <Image className="w-5 h-5" />
                </Button>
                <Input
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder={t('chat.typeMessage')}
                  onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSendMessage()}
                  className="flex-1"
                />
                <Button onClick={handleSendMessage} disabled={!newMessage.trim() || sendingMessage}>
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center text-muted-foreground">
            <div className="text-center">
              <MessageCircle className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>{t('chat.selectThread')}</p>
            </div>
          </div>
        )}
      </Card>

      {/* Create Thread Dialog */}
      <CreateThreadDialog 
        open={showCreateThread} 
        onOpenChange={setShowCreateThread}
        onCreated={() => { loadThreads(); setShowCreateThread(false); }}
      />

      {/* Invite Members Dialog */}
      {selectedThread && (
        <InviteMembersDialog
          open={showInviteMembers}
          onOpenChange={setShowInviteMembers}
          threadId={selectedThread.id}
          branchId={selectedThread.branch_id}
          onInvited={() => { loadThreads(); setShowInviteMembers(false); }}
        />
      )}
    </div>
  );
}

// Create Thread Dialog Component
function CreateThreadDialog({ open, onOpenChange, onCreated }) {
  const { t } = useTranslation();
  const [type, setType] = useState('group');
  const [title, setTitle] = useState('');
  const [branchId, setBranchId] = useState('');
  const [participantId, setParticipantId] = useState('');
  const [branches, setBranches] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      api.get('/branches').then(({ data }) => setBranches(data)).catch(() => {});
      api.get('/users').then(({ data }) => setUsers(data.filter(u => u.role === 'technician' || u.role === 'requester'))).catch(() => {});
    }
  }, [open]);

  const handleCreate = async () => {
    if (!title.trim()) {
      toast.error(t('chat.titleRequired'));
      return;
    }
    if (type === 'private' && !participantId) {
      toast.error(t('chat.participantRequired'));
      return;
    }
    
    setLoading(true);
    try {
      await api.post('/chat/threads', {
        type,
        title,
        branch_id: branchId || null,
        participant_user_id: type === 'private' ? participantId : null
      });
      toast.success(t('chat.threadCreated'));
      setTitle('');
      setBranchId('');
      setParticipantId('');
      onCreated();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border">
        <DialogHeader>
          <DialogTitle className="font-barlow">{t('chat.createThread')}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div>
            <label className="text-sm font-medium text-muted-foreground">{t('chat.threadType')}</label>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="group">{t('chat.group')}</SelectItem>
                <SelectItem value="private">{t('chat.private')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-sm font-medium text-muted-foreground">{t('chat.threadTitle')}</label>
            <Input 
              value={title} 
              onChange={(e) => setTitle(e.target.value)} 
              placeholder={t('chat.enterTitle')}
              className="mt-1"
            />
          </div>
          {type === 'group' && (
            <div>
              <label className="text-sm font-medium text-muted-foreground">{t('common.branch')}</label>
              <Select value={branchId} onValueChange={setBranchId}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder={t('workRequest.selectBranch')} />
                </SelectTrigger>
                <SelectContent>
                  {branches.map(b => (
                    <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          {type === 'private' && (
            <div>
              <label className="text-sm font-medium text-muted-foreground">{t('chat.participant')}</label>
              <Select value={participantId} onValueChange={setParticipantId}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder={t('chat.selectParticipant')} />
                </SelectTrigger>
                <SelectContent>
                  {users.map(u => (
                    <SelectItem key={u.id} value={u.id}>{u.name} ({u.role})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>{t('common.cancel')}</Button>
          <Button onClick={handleCreate} disabled={loading}>{t('common.create')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Invite Members Dialog Component
function InviteMembersDialog({ open, onOpenChange, threadId, branchId, onInvited }) {
  const { t } = useTranslation();
  const [technicians, setTechnicians] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      // Get technicians for this branch
      api.get('/users').then(({ data }) => {
        const techs = data.filter(u => 
          u.role === 'technician' && 
          (!branchId || u.allowed_branch_ids?.includes(branchId))
        );
        setTechnicians(techs);
      }).catch(() => {});
    }
  }, [open, branchId]);

  const handleInvite = async () => {
    if (selectedIds.length === 0) {
      toast.error(t('chat.selectTechnicians'));
      return;
    }
    
    setLoading(true);
    try {
      const { data } = await api.post(`/chat/threads/${threadId}/invite`, {
        user_ids: selectedIds
      });
      toast.success(t('chat.membersInvited', { count: data.added?.length || 0 }));
      setSelectedIds([]);
      onInvited();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border">
        <DialogHeader>
          <DialogTitle className="font-barlow">{t('chat.inviteMembers')}</DialogTitle>
        </DialogHeader>
        <div className="py-4">
          {technicians.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">{t('chat.noTechnicians')}</p>
          ) : (
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {technicians.map(tech => (
                <div
                  key={tech.id}
                  onClick={() => toggleSelect(tech.id)}
                  className={`p-3 rounded-lg cursor-pointer transition-colors ${
                    selectedIds.includes(tech.id) 
                      ? 'bg-brand/10 border border-brand/30' 
                      : 'bg-muted/30 hover:bg-muted/50'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-green-500/10 flex items-center justify-center">
                      <User className="w-4 h-4 text-green-500" />
                    </div>
                    <div>
                      <p className="font-medium text-foreground">{tech.name}</p>
                      <p className="text-xs text-muted-foreground">{tech.email}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>{t('common.cancel')}</Button>
          <Button onClick={handleInvite} disabled={loading || selectedIds.length === 0}>
            {t('chat.invite')} ({selectedIds.length})
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
